// Banner + Modal para emitir factura en FacturaDirecta desde una petición
import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FileText, Loader2, AlertCircle, Download, Mail, MessageCircle, RefreshCw,
  Trash2, Eye, Receipt, Sparkles, X as IconX, Copy, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

export function FacturaFDBanner({ peticion, onChange }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openEmit, setOpenEmit] = useState(false);
  const [openManage, setOpenManage] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [fecha, setFecha] = useState("");
  const [concepto, setConcepto] = useState("");
  const [syncingCli, setSyncingCli] = useState(false);
  const [albaranando, setAlbaranando] = useState(false);

  const load = async () => {
    if (!peticion?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/peticiones/${peticion.id}/factura-fd/preview`);
      setPreview(data);
      setEditLines(data.lineas || []);
      setFecha(new Date().toISOString().slice(0, 10));
    } catch (e) {
      console.warn("FD preview err", e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [peticion?.id]);

  const emitir = async () => {
    setEmitting(true);
    try {
      const { data } = await api.post(`/peticiones/${peticion.id}/factura-fd/emitir`, {
        fecha,
        concepto: concepto.trim() || null,
        lineas_override: editLines.length > 0 ? editLines : null,
      });
      toast.success(`Factura ${data.factura.fd_numero} emitida en FD ✨`);
      setOpenEmit(false);
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error emitiendo factura");
    } finally { setEmitting(false); }
  };

  const regenerar = async () => {
    if (!confirm("Esto anulará la factura actual y emitirá una nueva. ¿Continuar?")) return;
    setEmitting(true);
    try {
      const { data } = await api.post(`/peticiones/${peticion.id}/factura-fd/regenerar`, {
        fecha: fecha || undefined,
        concepto: concepto.trim() || null,
        lineas_override: editLines.length > 0 ? editLines : null,
      });
      toast.success(`Factura regenerada: ${data.factura.fd_numero}`);
      setOpenEmit(false);
      setOpenManage(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error regenerando");
    } finally { setEmitting(false); }
  };

  const anular = async () => {
    if (!confirm("¿Anular la factura en FacturaDirecta? Esta operación es contable.")) return;
    try {
      await api.post(`/peticiones/${peticion.id}/factura-fd/anular`);
      toast.success("Factura anulada");
      await load();    } catch (e) {
      toast.error(e.response?.data?.detail || "Error anulando");
    }
  };

  if (loading || !preview) return null;

  const factura = preview.factura_existente;
  const cliente = preview.cliente;

  // Ya emitida
  if (factura) {
    const bannerClass = factura.fd_anulada
      ? "border-rose-500/40 bg-rose-500/10"
      : factura.fd_state === "paid"
      ? "border-sky-500/40 bg-sky-500/10"
      : factura.fd_state === "overdue"
      ? "border-amber-500/40 bg-amber-500/10"
      : "border-emerald-500/40 bg-emerald-500/10";
    return (
      <>
        <div className={`mb-6 rounded-xl p-4 border-2 ${bannerClass}`}
             data-testid="banner-factura-fd-emitida">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-emerald-200 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                {factura.fd_anulada ? "Factura ANULADA"
                  : factura.fd_state === "paid" ? "Factura COBRADA ✓"
                  : factura.fd_state === "overdue" ? "Factura VENCIDA"
                  : "Factura FD emitida"} · <span className="font-mono">{factura.fd_numero}</span>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-zinc-300 mt-1.5">
                <span>💰 <b>{factura.fd_total?.toFixed(2)}€</b></span>
                {factura.fd_paid_amount > 0 && (
                  <span className="text-sky-300">✓ Cobrado <b>{factura.fd_paid_amount.toFixed(2)}€</b></span>
                )}
                {factura.fd_pending > 0.01 && (
                  <span className="text-amber-300">⏳ Pendiente <b>{factura.fd_pending.toFixed(2)}€</b></span>
                )}
                <span>📅 {factura.fd_fecha}</span>
                <span>⏰ Vence: {factura.fd_due_date}</span>
                <span className="text-zinc-500">Emitida por {factura.fd_emitida_por}</span>
              </div>
              {factura.fd_paid_amount > 0 && factura.fd_total > 0 && (
                <div className="w-full bg-zinc-800 h-1.5 rounded mt-2 overflow-hidden max-w-md">
                  <div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500"
                       style={{ width: `${Math.min(100, (factura.fd_paid_amount / factura.fd_total) * 100)}%` }} />
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {factura.fd_pdf_cloudinary_url && (
                <a href={factura.fd_pdf_cloudinary_url} target="_blank" rel="noreferrer" data-testid="btn-fd-pdf">
                  <Button size="sm" variant="outline" className="bg-transparent border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10">
                    <Download className="w-3 h-3 mr-1.5" /> PDF
                  </Button>
                </a>
              )}
              <Button size="sm" onClick={() => setOpenManage(true)} data-testid="btn-fd-gestionar"
                className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold">
                <Sparkles className="w-3 h-3 mr-1.5" /> Gestionar
              </Button>
            </div>
          </div>
        </div>

        <ManageDialog
          open={openManage} onClose={() => setOpenManage(false)}
          peticion={peticion} factura={factura}
          onAnular={anular} onRegenerar={() => { setOpenManage(false); setOpenEmit(true); }}
          onReloaded={load}
        />

        {/* Re-emisión: usamos el mismo EmitDialog */}
        <EmitDialog
          open={openEmit} onClose={() => setOpenEmit(false)}
          preview={preview}
          editLines={editLines} setEditLines={setEditLines}
          fecha={fecha} setFecha={setFecha}
          concepto={concepto} setConcepto={setConcepto}
          emitting={emitting}
          onEmit={regenerar}
          buttonLabel="Anular + emitir nueva factura"
          isRegen
        />
      </>
    );
  }

  // No emitida aún — mostrar banner solo si hay líneas facturables
  if (!preview.lineas || preview.lineas.length === 0) return null;

  const syncClienteFD = async () => {
    if (!cliente?.id) return;
    setSyncingCli(true);
    try {
      const { data } = await api.post(`/clientes/${cliente.id}/sync-fd`);
      if (data.cliente?.fd_id) {
        toast.success(`✓ ${cliente.razon_social || "Cliente"} sincronizado con FD`);
      } else {
        toast.warning("Sync intentada pero el cliente sigue sin fd_id. Revisa CIF.");
      }
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error sincronizando cliente");
    } finally {
      setSyncingCli(false);
    }
  };

  const albaranarPeticion = async () => {
    if (!window.confirm(
      "Vas a crear/añadir un albarán FD con los reportes ya montados de esta petición.\n\n" +
      "Se agruparán en el albarán FD abierto del mes para este cliente (o crea uno nuevo). ¿Continuar?"
    )) return;
    setAlbaranando(true);
    try {
      const { data } = await api.post(`/peticiones/${peticion.id}/enviar-fd`);
      if (data.fallos === 0) {
        toast.success(`✅ ${data.ok} reporte(s) albaranados en FD`);
      } else {
        const primerError = (data.resultados || []).find((r) => !r.ok)?.error || "—";
        toast.warning(`${data.ok} OK · ${data.fallos} fallos. ${primerError}`, { duration: 9000 });
      }
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error albaranando petición");
    } finally {
      setAlbaranando(false);
    }
  };

  return (
    <>
      <div className="mb-6 rounded-xl p-4 border border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5 flex items-center justify-between gap-3 flex-wrap"
           data-testid="banner-emitir-factura-fd">
        <div className="text-sm">
          <span className="text-amber-200 font-semibold flex items-center gap-1.5">
            <Receipt className="w-4 h-4" /> Listo para facturar
          </span>
          <span className="text-zinc-400 text-xs mt-0.5 inline-block">
            {preview.lineas.length} línea{preview.lineas.length !== 1 ? "s" : ""} · subtotal {preview.subtotal.toFixed(2)}€ · IVA {preview.iva.toFixed(2)}€ · <b>{preview.total.toFixed(2)}€</b>
            {!cliente.fd_sincronizado && (
              <button
                type="button"
                onClick={syncClienteFD}
                disabled={syncingCli}
                data-testid="sync-cliente-fd-btn"
                title={`Sincronizar "${cliente.razon_social || ""}" con FacturaDirecta`}
                className="text-rose-300 hover:text-rose-100 ml-2 inline-flex items-center gap-0.5 underline decoration-dotted"
              >
                {syncingCli ? <Loader2 className="w-3 h-3 animate-spin" /> : "⚠"} Cliente sin sync FD · Sincronizar ahora
              </button>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm"
            onClick={albaranarPeticion}
            disabled={albaranando || !cliente.fd_sincronizado}
            data-testid="albaranar-peticion-fd-btn"
            title={cliente.fd_sincronizado
              ? "Crea/añade albarán FD con los reportes ya montados (sin emitir factura todavía)"
              : "Sincroniza el cliente con FD primero"}
            variant="outline"
            className="bg-transparent border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-semibold">
            {albaranando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : "📤"} Crear/añadir albarán FD
          </Button>
          <Button size="sm" onClick={() => setOpenEmit(true)} data-testid="emitir-factura-fd-btn"
            disabled={!cliente.fd_sincronizado}
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
            <Receipt className="w-4 h-4 mr-1.5" /> Emitir factura FD
          </Button>
        </div>
      </div>

      <EmitDialog
        open={openEmit} onClose={() => setOpenEmit(false)}
        preview={preview}
        editLines={editLines} setEditLines={setEditLines}
        fecha={fecha} setFecha={setFecha}
        concepto={concepto} setConcepto={setConcepto}
        emitting={emitting}
        onEmit={emitir}
        buttonLabel="Emitir factura"
      />
    </>
  );
}

function EmitDialog({ open, onClose, preview, editLines, setEditLines, fecha, setFecha, concepto, setConcepto, emitting, onEmit, buttonLabel, isRegen }) {
  const subtotal = editLines.reduce((s, ln) => s + Number(ln.lineTotal || 0), 0);
  const iva = subtotal * 0.21;
  const total = subtotal + iva;

  const updateLine = (idx, field, value) => {
    const copy = [...editLines];
    copy[idx] = { ...copy[idx], [field]: value };
    if (field === "quantity" || field === "unitPrice") {
      copy[idx].lineTotal = Number(copy[idx].quantity || 0) * Number(copy[idx].unitPrice || 0);
    }
    setEditLines(copy);
  };

  const removeLine = (idx) => setEditLines(editLines.filter((_, i) => i !== idx));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[90vh] overflow-auto" data-testid="modal-emitir-factura-fd">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-amber-400" /> {isRegen ? "Regenerar factura FD" : "Emitir factura en FacturaDirecta"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Cliente */}
          <div className="bg-zinc-900/40 rounded-lg p-3 border border-zinc-800">
            <div className="text-xs text-zinc-500">Cliente</div>
            <div className="text-zinc-200 font-semibold">{preview.cliente.nombre}</div>
            <div className="text-xs text-zinc-400 mt-0.5">CIF: {preview.cliente.cif} · FD ID: <span className="font-mono">{preview.cliente.fd_id || "—"}</span></div>
            {!preview.cliente.fd_sincronizado && (
              <div className="text-rose-400 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Cliente sin sincronizar en FD</div>
            )}
          </div>

          {/* Fecha + Concepto */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Fecha factura</Label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="bg-zinc-900 border-zinc-700 mt-1" data-testid="input-fd-fecha" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Concepto / Nota interna <span className="text-zinc-600">(opcional)</span></Label>
              <Input value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Ej: Campaña SS26" className="bg-zinc-900 border-zinc-700 mt-1" data-testid="input-fd-concepto" />
            </div>
          </div>

          {/* Líneas */}
          <div>
            <Label className="text-xs text-zinc-400 mb-2 block">Líneas ({editLines.length})</Label>
            <div className="space-y-2 max-h-96 overflow-auto">
              {editLines.map((ln, i) => (
                <div key={i} className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 space-y-2" data-testid={`fd-line-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input
                      value={ln.text}
                      onChange={(e) => updateLine(i, "text", e.target.value)}
                      className="bg-zinc-950 border-zinc-700 text-sm flex-1"
                      data-testid={`fd-line-text-${i}`}
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeLine(i)} data-testid={`fd-line-remove-${i}`}>
                      <Trash2 className="w-3 h-3 text-rose-400" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <Label className="text-[10px] text-zinc-500">Cantidad</Label>
                      <Input type="number" step="0.01" value={ln.quantity} onChange={(e) => updateLine(i, "quantity", parseFloat(e.target.value) || 0)} className="bg-zinc-950 border-zinc-700 h-8 text-xs" data-testid={`fd-line-qty-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-500">Precio unit. (€)</Label>
                      <Input type="number" step="0.01" value={ln.unitPrice} onChange={(e) => updateLine(i, "unitPrice", parseFloat(e.target.value) || 0)} className="bg-zinc-950 border-zinc-700 h-8 text-xs" data-testid={`fd-line-price-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-500">Total línea</Label>
                      <div className="h-8 px-3 bg-zinc-950 border border-zinc-700 rounded flex items-center text-xs text-amber-300 font-mono">{(Number(ln.lineTotal) || 0).toFixed(2)}€</div>
                    </div>
                  </div>
                </div>
              ))}
              {editLines.length === 0 && (
                <div className="text-zinc-500 text-xs text-center py-4">Sin líneas. Añade componentes a la petición primero.</div>
              )}
            </div>
          </div>

          {/* Totales */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm">
            <div className="flex justify-between text-zinc-300"><span>Subtotal</span><span className="font-mono">{subtotal.toFixed(2)}€</span></div>
            <div className="flex justify-between text-zinc-300"><span>IVA 21%</span><span className="font-mono">{iva.toFixed(2)}€</span></div>
            <div className="flex justify-between text-amber-200 font-bold text-lg border-t border-amber-500/30 mt-1 pt-1"><span>TOTAL</span><span className="font-mono">{total.toFixed(2)}€</span></div>
          </div>

          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline" className="flex-1">Cancelar</Button>
            <Button onClick={onEmit} disabled={emitting || editLines.length === 0} data-testid="confirmar-emitir-fd-btn"
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
              {emitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              {buttonLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManageDialog({ open, onClose, peticion, factura, onAnular, onRegenerar, onReloaded }) {
  const [whatsappData, setWhatsappData] = useState(null);
  const [loadingWA, setLoadingWA] = useState(false);
  const [email, setEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [regeneratingPDF, setRegeneratingPDF] = useState(false);
  const [syncingCobros, setSyncingCobros] = useState(false);
  const [cobrosData, setCobrosData] = useState(null);

  const buildWhatsApp = async () => {
    setLoadingWA(true);
    try {
      const { data } = await api.get(`/peticiones/${peticion.id}/factura-fd/whatsapp-link`);
      setWhatsappData(data);
    } catch (e) {
      toast.error("Error generando link WhatsApp");
    } finally { setLoadingWA(false); }
  };

  const sendEmail = async () => {
    if (!email) {
      toast.error("Indica un email");
      return;
    }
    setSendingEmail(true);
    try {
      await api.post(`/peticiones/${peticion.id}/factura-fd/enviar-email`, { to: email });
      toast.success("Email enviado por FD");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando email");
    } finally { setSendingEmail(false); }
  };

  const regenPDF = async () => {
    setRegeneratingPDF(true);
    try {
      await api.post(`/peticiones/${peticion.id}/factura-fd/regenerar-pdf`);
      toast.success("PDF regenerado");
      if (onReloaded) onReloaded();
    } catch (e) {
      toast.error("Error regenerando PDF");
    } finally { setRegeneratingPDF(false); }
  };

  const syncCobros = async () => {
    setSyncingCobros(true);
    try {
      const { data } = await api.get(`/peticiones/${peticion.id}/factura-fd/cobros`);
      setCobrosData(data);
      toast.success(`Sincronizado: cobrado ${data.fd_paid_amount}€ / pendiente ${data.fd_pending}€`);
      if (onReloaded) onReloaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error sincronizando cobros");
    } finally { setSyncingCobros(false); }
  };

  const copyMsg = () => {
    navigator.clipboard.writeText(whatsappData.mensaje_whatsapp);
    toast.success("Mensaje copiado");
  };

  const openWA = () => {
    const phone = (whatsappData.telefono || "").replace(/[^\d+]/g, "");
    const text = encodeURIComponent(whatsappData.mensaje_whatsapp);
    const url = phone ? `https://wa.me/${phone.replace("+", "")}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="modal-fd-gestionar">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-400" /> Factura {factura.fd_numero}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {factura.fd_anulada && (
            <div className="bg-rose-500/10 border border-rose-500/40 rounded p-2 text-rose-200 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Esta factura está anulada en FD ({factura.fd_anulada_at?.slice(0, 10)})
            </div>
          )}

          {/* WhatsApp */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
            <div className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5 mb-2">
              <MessageCircle className="w-3 h-3" /> Enviar por WhatsApp
            </div>
            {!whatsappData ? (
              <Button onClick={buildWhatsApp} disabled={loadingWA} size="sm" data-testid="btn-fd-wa-gen"
                className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 w-full">
                {loadingWA ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MessageCircle className="w-3 h-3 mr-1" />}
                Generar mensaje
              </Button>
            ) : (
              <>
                <Textarea value={whatsappData.mensaje_whatsapp} readOnly rows={5} className="bg-zinc-950 border-zinc-700 text-xs font-mono mb-2" data-testid="fd-wa-textarea" />
                <div className="flex gap-2">
                  <Button onClick={copyMsg} variant="outline" size="sm" className="flex-1"><Copy className="w-3 h-3 mr-1" /> Copiar</Button>
                  <Button onClick={openWA} size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold" data-testid="btn-fd-wa-open">
                    <MessageCircle className="w-3 h-3 mr-1" /> Abrir WhatsApp
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Email */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
            <div className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5 mb-2">
              <Mail className="w-3 h-3" /> Enviar por email <span className="text-zinc-500 font-normal">(desde FacturaDirecta)</span>
            </div>
            <div className="flex gap-2">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@ejemplo.com" className="bg-zinc-950 border-zinc-700 text-xs h-8" data-testid="input-fd-email" />
              <Button onClick={sendEmail} disabled={sendingEmail || !email} size="sm" data-testid="btn-fd-email-send"
                className="bg-sky-500 hover:bg-sky-400 text-zinc-900 font-bold">
                {sendingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              </Button>
            </div>
            {factura.fd_enviada_email_at && (
              <div className="text-emerald-300 text-[10px] mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Enviada a {factura.fd_enviada_email_to} · {factura.fd_enviada_email_at.slice(0, 16).replace("T", " ")}</div>
            )}
          </div>

          {/* Acciones secundarias */}
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={syncCobros} disabled={syncingCobros} variant="outline" size="sm" data-testid="btn-fd-sync-cobros"
              className="text-xs bg-sky-500/10 border-sky-500/40 text-sky-200 hover:bg-sky-500/20">
              {syncingCobros ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Receipt className="w-3 h-3 mr-1" />}
              Sincronizar cobros
            </Button>
            <Button onClick={regenPDF} disabled={regeneratingPDF} variant="outline" size="sm" data-testid="btn-fd-pdf-regen"
              className="text-xs">
              {regeneratingPDF ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Regenerar PDF
            </Button>
            <Button onClick={onRegenerar} variant="outline" size="sm" className="text-xs bg-amber-500/10 border-amber-500/40 text-amber-200 hover:bg-amber-500/20 col-span-2" data-testid="btn-fd-regenerar-factura">
              <Sparkles className="w-3 h-3 mr-1" /> Anular + emitir nueva
            </Button>
          </div>

          {/* Detalle de cobros tras sync */}
          {cobrosData && (
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 space-y-2" data-testid="cobros-detail">
              <div className="text-xs font-semibold text-zinc-300">Pagos registrados en FD</div>
              <div className="text-[11px] text-zinc-400">
                Total: <b className="text-zinc-200">{cobrosData.fd_total.toFixed(2)}€</b> · Cobrado: <b className="text-sky-300">{cobrosData.fd_paid_amount.toFixed(2)}€</b> · Pendiente: <b className="text-amber-300">{cobrosData.fd_pending.toFixed(2)}€</b>
              </div>
              {cobrosData.pagos.length === 0 ? (
                <div className="text-[10px] text-zinc-500">Sin pagos registrados aún.</div>
              ) : (
                <div className="space-y-1 mt-2">
                  {cobrosData.pagos.map((p, i) => (
                    <div key={i} className="flex justify-between text-[10px] bg-zinc-950/50 rounded p-1.5">
                      <span className="text-zinc-400">{p.fecha} · {p.tipo || "—"}</span>
                      <span className="text-emerald-300 font-mono">+{p.monto.toFixed(2)}€</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!factura.fd_anulada && (
            <Button onClick={onAnular} variant="outline" size="sm" data-testid="btn-fd-anular"
              className="w-full text-xs bg-rose-500/10 border-rose-500/40 text-rose-200 hover:bg-rose-500/20">
              <IconX className="w-3 h-3 mr-1" /> Anular factura
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
